import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_BG = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80';

export function ContactPage() {
  const { i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    need_type: '',
    message: '',
    language: 'fr'
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  const subjects = [
    { value: 'accessories', label: getText('Accessoires Mobiles', 'إكسسوارات الهواتف', 'Mobile Accessories') },
    { value: 'telecom', label: getText('Produits Télécom', 'منتجات الاتصالات', 'Telecom Products') },
    { value: 'retailtech', label: getText("Bureau d'Étude", 'مكتب دراسات', 'Engineering') },
    { value: 'partnership', label: getText('Partenariat', 'شراكة', 'Partnership') },
    { value: 'other', label: getText('Autre', 'أخرى', 'Other') }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, language: i18n.language }),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: getText('Message envoyé avec succès !', 'تم إرسال الرسالة بنجاح!', 'Message sent successfully!')
        });
        setFormData({ name: '', email: '', company: '', phone: '', need_type: '', message: '', language: i18n.language });
      } else {
        throw new Error('Error');
      }
    } catch {
      setStatus({
        type: 'error',
        message: getText('Erreur. Veuillez réessayer.', 'خطأ. حاول مرة أخرى.', 'Error. Please try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F8F6F3]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img src={CONTACT_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>
        
        <div className="container-main relative z-10 py-24">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="text-marquee-label text-white/80 mb-6 block">
              {getText('CONTACT RAPIDE', 'تواصل سريع', 'QUICK ENQUIRY')}
            </span>
            <h1 className="text-hero text-white mb-6">
              {getText('Parlons de votre projet', 'لنتحدث عن مشروعك', "Let's talk about your project")}
            </h1>
            <p className="text-xl text-white/60">
              {getText(
                'Conseils experts pour vos projets télécom, retail ou industriels.',
                'استشارات خبراء لمشاريعكم في الاتصالات والتجزئة أو الصناعة.',
                'Expert advice for your telecom, retail or industrial projects.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section with asymmetric background */}
      <section className="relative -mt-10 z-10" data-testid="form-section">
        <div className="container-main">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative overflow-hidden rounded-[40px]"
            style={{ borderRadius: '40px 40px 100px 40px' }}
          >
            {/* Background */}
            <div className="absolute inset-0 bg-white" />
            
            {/* Decorative shapes */}
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#f4b00f]/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[#f4b00f]/5 rounded-full blur-2xl" />
            
            <div className="relative z-10 p-10 md:p-16 lg:p-20">
              <div className="grid lg:grid-cols-3 gap-16">
                {/* Contact Info */}
                <div>
                  <h2 className="text-2xl font-semibold mb-8">
                    {getText('Coordonnées', 'معلومات الاتصال', 'Contact Info')}
                  </h2>
                  <div className="space-y-6">
                    {[
                      { icon: Mail, label: 'Email', value: 'contact@simplyd.one', href: 'mailto:contact@simplyd.one' },
                      { icon: Phone, label: getText('Téléphone', 'الهاتف', 'Phone'), value: '+33 4 91 91 81 76', href: 'tel:+33491918176' }
                    ].map((item) => (
                      <div key={item.label} className="flex items-start gap-4 group">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ffcc4d] via-[#f4b00f] to-[#d99a00] flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110">
                          <item.icon className="w-5 h-5 text-black" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-400 mb-1">{item.label}</div>
                          {item.href ? (
                            <a href={item.href} className="font-medium hover:text-[#f4b00f] transition-colors">{item.value}</a>
                          ) : (
                            <span className="font-medium">{item.value}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Addresses */}
                  <div className="mt-8 space-y-5">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {getText('Nos Adresses', 'عناويننا', 'Our Addresses')}
                    </h3>
                    
                    {/* Siège social */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-full bg-[#f4b00f]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-[#f4b00f]" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#f4b00f] font-semibold mb-1">
                          {getText('Siège social', 'المقر الرئيسي', 'Headquarters')}
                        </div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          30A Allée de la Palun<br />
                          Local 132-286<br />
                          13700 Marignane
                        </div>
                      </div>
                    </div>
                    
                    {/* Administratif */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-full bg-[#f4b00f]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-[#f4b00f]" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#f4b00f] font-semibold mb-1">
                          {getText('Administratif', 'إداري', 'Administrative')}
                        </div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          5 rue Edmonde Charles Roux<br />
                          54000 Nancy
                        </div>
                      </div>
                    </div>
                    
                    {/* Entrepôt */}
                    <div className="flex items-start gap-4 group">
                      <div className="w-10 h-10 rounded-full bg-[#f4b00f]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-[#f4b00f]" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#f4b00f] font-semibold mb-1">
                          {getText('Entrepôt', 'المستودع', 'Warehouse')}
                        </div>
                        <div className="text-sm text-gray-600 leading-relaxed">
                          2 rue Jean Moulin<br />
                          93350 Le Bourget
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Response time badge */}
                  <div className="mt-10 p-5 bg-[#F8F6F3] rounded-[20px]">
                    <div className="text-xs uppercase tracking-widest text-[#f4b00f] mb-2">
                      {getText('Temps de réponse', 'وقت الاستجابة', 'Response time')}
                    </div>
                    <div className="text-2xl font-bold">24h</div>
                    <div className="text-sm text-gray-500">
                      {getText('en moyenne', 'في المتوسط', 'on average')}
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="lg:col-span-2">
                  <div className="mb-8">
                    <span className="text-marquee-label mb-4 block">
                      {getText('FORMULAIRE', 'النموذج', 'FORM')}
                    </span>
                    <h3 className="text-title">
                      {getText('Décrivez votre besoin', 'صف احتياجك', 'Describe your need')}
                    </h3>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Subject select first like Spaciaz */}
                    <select
                      name="need_type"
                      value={formData.need_type}
                      onChange={handleChange}
                      required
                      className="select-modern"
                      data-testid="select-need"
                    >
                      <option value="" disabled>
                        {getText('Votre demande concerne... *', 'طلبك يتعلق بـ... *', 'Your inquiry about... *')}
                      </option>
                      {subjects.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>

                    <p className="text-sm text-gray-500">
                      {getText(
                        "Nous sommes impatients de vous rencontrer ! Les champs marqués * sont obligatoires.",
                        'نحن متحمسون للتواصل معك! الحقول المميزة بـ * مطلوبة.',
                        "We're excited to connect with you! Fields marked * are required."
                      )}
                    </p>

                    <div className="grid md:grid-cols-2 gap-5">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={getText('Nom complet *', 'الاسم الكامل *', 'Full name *')}
                        required
                        className="input-modern"
                        data-testid="input-name"
                      />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={getText('Email professionnel *', 'البريد المهني *', 'Professional email *')}
                        required
                        className="input-modern"
                        data-testid="input-email"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={getText('Entreprise', 'الشركة', 'Company')}
                        className="input-modern"
                        data-testid="input-company"
                      />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={getText('Téléphone', 'الهاتف', 'Phone')}
                        className="input-modern"
                        data-testid="input-phone"
                      />
                    </div>

                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={getText('Décrivez votre projet ou besoin *', 'صف مشروعك أو احتياجك *', 'Describe your project or need *')}
                      required
                      className="textarea-modern"
                      data-testid="textarea-message"
                    />

                    {status.message && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex items-center gap-3 p-5 rounded-[20px] ${
                          status.type === 'success' 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}
                      >
                        {status.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        <span className="font-medium">{status.message}</span>
                      </motion.div>
                    )}

                    <button 
                      type="submit" 
                      disabled={isSubmitting} 
                      className="btn-primary"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {getText('Envoi...', 'إرسال...', 'Sending...')}
                        </span>
                      ) : (
                        <>
                          {getText('Envoyer ma demande', 'إرسال طلبي', 'Send my request')}
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-32" />
    </div>
  );
}

export default ContactPage;
